<template>
  <div class="popular-list-container" :class="theme === 'dark' ? 'theme-dark' : 'theme-light'" v-if="topic_id">
    <div v-if="loading" class="text-center py-4">
      <b-spinner small variant="primary"></b-spinner>
    </div>
    <div v-else-if="stocks.length === 0" class="text-center py-5 text-muted small empty-box" style="min-height: 150px; display: flex; align-items: center; justify-content: center;">
      暂无数据
    </div>
    <div v-else class="topic-items-container">
      <div 
        v-for="stock in stocks.slice(0, topN)" 
        :key="stock.stock_code"
        class="popular-stock-card transition-all"
        :class="getRowHighlightClass(stock.stock_name)"
        v-b-toggle.sidebar-right-stock
        @click="handleStockClick(stock)"
        :title="`${stock.stock_name} (${stock.stock_code}) - 人气排名: ${stock.rank}`"
      >
        <div class="d-flex align-items-center justify-content-between w-100">
          <!-- Left: Rank and Diff -->
          <div class="d-flex align-items-center flex-shrink-0" style="min-width: 48px;">
            <span class="stock-rank-badge" :class="getRankClass(stock.rank)">{{ stock.rank }}</span>
            <span class="stock-rank-diff ml-1" :class="getDiffClass(stock.rank_diff)">
              {{ formatDiff(stock.rank_diff) }}
            </span>
          </div>
          
          <!-- Middle: Stock Name -->
          <span class="stock-name-label text-truncate text-left px-1 flex-grow-1 font-weight-bold">
            {{ stock.stock_name }}
          </span>
          
          <!-- Right: Change Rate -->
          <span class="stock-rate-label flex-shrink-0 text-right code-font font-weight-bold" :class="stock.px_change_rate >= 0 ? 'text-up' : 'text-down'">
            {{ stock.px_change_rate >= 0 ? '+' : '' }}{{ stock.px_change_rate ? stock.px_change_rate.toFixed(2) : '0.00' }}%
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { request_zzquant } from "network/request.js"

export default {
  name: "TopicStockPopularTable",
  props: {
    topic_id: {
      type: [Number, String],
      required: true
    },
    date1: {
      type: String,
      required: true
    },
    topN: {
      type: Number,
      default: 10
    },
    latestTopThree: {
      type: Array,
      default: () => []
    },
    clickedStockName: {
      type: String,
      default: null
    },
    isLatest: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'light'
    }
  },
  data() {
    return {
      loading: false,
      stocks: []
    };
  },
  watch: {
    topic_id: {
      immediate: true,
      handler(newVal) {
        if (newVal) this.fetchPopularStocks();
      }
    },
    date1: {
      immediate: true,
      handler(newVal) {
        if (newVal) this.fetchPopularStocks();
      }
    }
  },
  methods: {
    fetchPopularStocks() {
      if (!this.topic_id || !this.date1) return;
      this.loading = true;
      request_zzquant({
        url: `/v3/topic/table/${this.topic_id}/stocks/popular`,
        method: 'GET',
        params: {
          date1: this.date1,
          limit: 100
        }
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          const list = (res.data.data || []).filter(s => s.rank < 9999);
          this.stocks = list;
          if (this.isLatest && list.length > 0) {
            const topThree = list.slice(0, 3).map(s => s.stock_name);
            this.$emit("latest-top-three", topThree);
          }
        } else {
          this.stocks = [];
        }
      })
      .catch(e => {
        console.error(`Failed to fetch popular stocks for topic ${this.topic_id} on ${this.date1}:`, e);
        this.stocks = [];
      })
      .finally(() => {
        this.loading = false;
      });
    },
    handleStockClick(stock) {
      this.$emit("stock-clicked", stock.stock_name);
      this.$emit("show_sidebar", {
        stock_code: stock.stock_code,
        stock_name: stock.stock_name,
        date1: this.date1
      });
    },
    getRankClass(rank) {
      if (rank === 1) return 'rank-1';
      if (rank === 2) return 'rank-2';
      if (rank === 3) return 'rank-3';
      return 'rank-other';
    },
    getDiffClass(diff) {
      if (diff === undefined || diff === null || diff === 0) return 'diff-same';
      return diff > 0 ? 'diff-up' : 'diff-down';
    },
    formatDiff(diff) {
      if (diff === undefined || diff === null || diff === 0) return '=';
      return diff > 0 ? `+${diff}` : `${diff}`;
    },
    getRowHighlightClass(stockName) {
      if (this.clickedStockName && this.clickedStockName === stockName) {
        return 'highlight-clicked';
      }
      if (this.latestTopThree && this.latestTopThree.length > 0) {
        if (this.latestTopThree[0] === stockName) return 'highlight-top-1';
        if (this.latestTopThree[1] === stockName) return 'highlight-top-2';
        if (this.latestTopThree[2] === stockName) return 'highlight-top-3';
      }
      return '';
    }
  }
};
</script>

<style scoped>
.popular-list-container {
  display: flex;
  flex-direction: column;
  min-height: 100px;
}

.topic-items-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.popular-stock-card {
  display: flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  background-color: #ffffff;
  border-radius: 6px;
  padding: 6px 8px;
  margin-bottom: 6px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.theme-dark .popular-stock-card {
  border-color: #374151;
  background-color: #1f2937;
}

.popular-stock-card:hover {
  background-color: #f3f4f6;
  transform: translateY(-1px);
}

.theme-dark .popular-stock-card:hover {
  background-color: #374151;
}

.stock-rank-badge {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-size: 10px;
  font-weight: bold;
  flex-shrink: 0;
}

.stock-rank-badge.rank-1 {
  background-color: #dc3545 !important;
  color: #ffffff !important;
}

.stock-rank-badge.rank-2 {
  background-color: #fd7e14 !important;
  color: #ffffff !important;
}

.stock-rank-badge.rank-3 {
  background-color: #ffc107 !important;
  color: #1f2937 !important;
}

.stock-rank-badge.rank-other {
  background-color: #f3f4f6;
  color: #1f2937;
}

.theme-dark .stock-rank-badge.rank-other {
  background-color: #374151;
  color: #f3f4f6;
}

.stock-rank-diff {
  font-size: 9px;
  font-weight: bold;
  min-width: 22px;
  text-align: center;
  flex-shrink: 0;
}

.diff-up {
  color: #ef4444 !important;
}

.diff-down {
  color: #10b981 !important;
}

.diff-same {
  color: #6b7280;
}

.theme-dark .diff-same {
  color: #9ca3af;
}

.stock-name-label {
  font-size: 12px;
  font-weight: 500;
  color: #1f2937;
  max-width: 70px;
}

.theme-dark .stock-name-label {
  color: #f3f4f6;
}

.stock-rate-label {
  font-size: 11px;
}

.text-up {
  color: #ef4444 !important;
}
.text-down {
  color: #10b981 !important;
}

.code-font {
  font-family: 'Courier New', Courier, monospace;
}

.empty-box {
  background-color: #ffffff;
  border: 1px dashed #e5e7eb;
  border-radius: 6px;
}

.theme-dark .empty-box {
  background-color: #1f2937;
  border-color: #374151;
}

/* Highlight Styles for Popularity Cards */
.highlight-top-1 {
  background-color: rgba(220, 53, 69, 0.08) !important;
  box-shadow: inset 0 0 0 1px rgba(220, 53, 69, 0.35);
  border-color: rgba(220, 53, 69, 0.4) !important;
}
.highlight-top-2 {
  background-color: rgba(253, 126, 20, 0.08) !important;
  box-shadow: inset 0 0 0 1px rgba(253, 126, 20, 0.35);
  border-color: rgba(253, 126, 20, 0.4) !important;
}
.highlight-top-3 {
  background-color: rgba(255, 193, 7, 0.08) !important;
  box-shadow: inset 0 0 0 1px rgba(255, 193, 7, 0.35);
  border-color: rgba(255, 193, 7, 0.4) !important;
}
.highlight-clicked {
  background-color: rgba(139, 92, 246, 0.1) !important;
  box-shadow: inset 0 0 0 1.5px rgba(139, 92, 246, 0.5);
  border-color: rgba(139, 92, 246, 0.6) !important;
}

/* Border color for light/dark mode custom overrides */
.theme-dark .highlight-top-1 {
  background-color: rgba(239, 68, 68, 0.12) !important;
  box-shadow: inset 0 0 0 1px rgba(239, 68, 68, 0.45);
}
.theme-dark .highlight-top-2 {
  background-color: rgba(253, 126, 20, 0.12) !important;
  box-shadow: inset 0 0 0 1px rgba(253, 126, 20, 0.45);
}
.theme-dark .highlight-top-3 {
  background-color: rgba(245, 158, 11, 0.12) !important;
  box-shadow: inset 0 0 0 1px rgba(245, 158, 11, 0.45);
}
.theme-dark .highlight-clicked {
  background-color: rgba(139, 92, 246, 0.15) !important;
  box-shadow: inset 0 0 0 1.5px rgba(139, 92, 246, 0.7);
}

.transition-all {
  transition: all 0.25s ease;
}
</style>
